// Ngrx Entity
import { EntityState, createEntityAdapter } from '@ngrx/entity';

// Models
import { CartItem, CartItemHelper, CartItemAdd } from './cartItem';
import { CartPromo } from './cartPromo';
import { Product } from './product';
import { SaldoPromocionesItem } from './saldoPromocionesItem';

export enum CartItemStatus {
    Validated,
    Validating
}

/**
 * Type of timeshipping
 *
 * @export
 * @enum {number}
 */
export enum ShippingType {
    Date,
    OneDay,
    Today
}

/**
 * Status of cart
 *
 * @export
 * @enum {number}
 */
export enum CartStatus {
    Initialized = 1,
    Validated = 2,
    Validating = 3,
    Error = 4
}

/**
 * Possibles actions of cart
 *
 * @export
 * @enum {number}
 */
export enum CartAction {
    ItemAdded,
    ItemDeleted,
    ItemQuantityChanged,
    ItemPriceChanged,
    ItemDto1Changed,
    ItemDto2Changed,
    ItemPartDeleted,
    ItemShippingChanged
}

export interface Cart {
    total: number;
    totalMostrar: number;
    numItems: number;
    items: EntityState<CartItem>;
    promos: Array<CartPromo>;
    lastAction: CartAction;
    sessionId: string;
    promoCode: string;
    idPromo: string;
    saldoPromociones: SaldoPromocionesItem[];
    saldoConseguido: number;
}

export const cartItemAdapter = createEntityAdapter<CartItem>({
    selectId: (cartItem) => cartItem.product_id
});

/**
 * Cart
 *
 * @export
 * @class Cart
 */
export class CartHelper {
    static readonly unavailableStockTime = 'consultar disponibilidad';

    static create(): Cart {
        return {
            total: 0,
            totalMostrar: 0,
            numItems: 0,
            items: cartItemAdapter.getInitialState(),
            promos: [],
            lastAction: undefined,
            sessionId: '',
            promoCode: '',
            idPromo: '',
            saldoPromociones: null,
            saldoConseguido: 0
        };
    }

    static getItems(cart: Cart) {
        const { selectAll } = cartItemAdapter.getSelectors();
        return selectAll(cart.items);
    }

    static hasItemsWithoutConfirmedStock(items: CartItem[] | null | undefined): boolean {
        if (!items || !items.length) {
            return false;
        }

        return items.some((item) =>
            item.shippings?.some(
                (shipping) =>
                    typeof shipping?.tiempo === 'string' &&
                    shipping.tiempo.trim().toLowerCase() === CartHelper.unavailableStockTime
            )
        );
    }

    static calculateTotalQuantity(quantity: number, cart: Cart, product: Product) {
        const items = CartHelper.getItems(cart);
        let newQuantity = quantity;
        items.map((row) => {
            if (row.product_id === product.info.codigo) {
                newQuantity += row.quantity;
            }
        });

        return newQuantity;
    }

    /**
     * Add new product to cart
     *
     * @param {Product} product
     * @param {number} quantity
     * @param {CartItemStatus} status
     * @returns {Cart}
     *
     * @memberOf Cart
     */
    static add(cart: Cart, product: Product, quantity: number, status: CartItemStatus): Cart {
        let c = { ...cart };
        const newQuantity = CartHelper.calculateTotalQuantity(quantity, cart, product);
        const cartItem = CartItemHelper.create(product.info.codigo, product, newQuantity, status);

        c.items = cartItemAdapter.upsertOne(cartItem, cart.items);

        CartHelper.calculate(c);
        c.lastAction = CartAction.ItemAdded;
        return c;
    }

    static addMultiple(cart: Cart, items: CartItemAdd[], status: CartItemStatus): Cart {
        items.forEach((cartItemAdd) => {
            cart = CartHelper.add(cart, cartItemAdd.product, cartItemAdd.quantity, status);
        });

        return cart;
    }

    /**
     * Add part item
     *
     * @param {Cart} cart
     * @param {CartItem} cartItem
     * @returns {Cart}
     *
     */
    static addPart(cart: Cart, cartItem: CartItem): Cart {
        cart.items = cartItemAdapter.upsertOne(cartItem, cart.items);

        CartHelper.calculate(cart);
        cart.lastAction = CartAction.ItemAdded;
        return cart;
    }
    /**
     * Remove item from cart
     *
     * @param {Cart} cart
     * @param {CartItem} item
     * @returns {Cart}
     *
     * @memberOf Cart
     */
    static removeItem(cart: Cart, item: CartItem): Cart {
        cart.items = cartItemAdapter.removeOne(item.product_id, cart.items);

        CartHelper.calculate(cart);
        cart.numItems <= 0 ? (cart.promos = []) : '';

        cart.lastAction = item.parts ? CartAction.ItemPartDeleted : CartAction.ItemDeleted;
        return cart;
    }

    /**
     * Removes all part items in the cart
     *
     * @param cart
     */
    static removePartsItems(cart: Cart): void {
        const cartItems = CartHelper.getItems(cart);

        for (const item of cartItems) {
            if (item.parts) {
                CartHelper.removeItem(cart, item);
            }
        }

        CartHelper.calculate(cart);
    }

    /**
     * Change quantity of item in cart
     *
     * @param {Cart} cart
     * @param {CartItem} item
     * @param {number} quantity
     * @returns {Cart}
     *
     * @memberOf Cart
     */
    static changeQuantity(cart: Cart, item: CartItem, quantity: number): Cart {
        const cartItem: CartItem = CartItemHelper.setQuantity(item, quantity);

        cart.items = cartItemAdapter.upsertOne(cartItem, cart.items);

        CartHelper.calculate(cart);
        cart.lastAction = CartAction.ItemQuantityChanged;
        return cart;
    }

    /**
     * Change price of item in cart
     *
     * @param {Cart} cart
     * @param {CartItem} item
     * @param {number} price
     * @returns {Cart}
     *
     * @memberOf Cart
     */
    static changePrice(cart: Cart, item: CartItem, price: number): Cart {
        const cartItemPrice: CartItem = CartItemHelper.setPrice(item, price);

        cart.items = cartItemAdapter.upsertOne(cartItemPrice, cart.items);

        CartHelper.calculate(cart);
        cart.lastAction = CartAction.ItemPriceChanged;

        // console.log('changePrice', cart);

        return cart;
    }

    /**
     * Change Dto1 of item in cart
     *
     * @param {Cart} cart
     * @param {CartItem} item
     * @param {number} dto1
     * @returns {Cart}
     *
     * @memberOf Cart
     */
    static changeDto1(cart: Cart, item: CartItem, discount?: number): Cart {
        const cartItemDiscount: CartItem = CartItemHelper.setDiscount1(item, discount);

        cart.items = cartItemAdapter.upsertOne(cartItemDiscount, cart.items);

        CartHelper.calculate(cart);
        cart.lastAction = CartAction.ItemDto1Changed;

        // console.log('changeDto1', cart);

        return cart;
    }

    /**
     * Change Dto2 of item in cart
     *
     * @param {Cart} cart
     * @param {CartItem} item
     * @param {number} dto2
     * @returns {Cart}
     *
     * @memberOf Cart
     */
    static changeDto2(cart: Cart, item: CartItem, discount?: number): Cart {
        const cartItemDiscount: CartItem = CartItemHelper.setDiscount2(item, discount);

        cart.items = cartItemAdapter.upsertOne(cartItemDiscount, cart.items);

        CartHelper.calculate(cart);
        cart.lastAction = CartAction.ItemDto2Changed;

        // console.log('changeDto2', cart);

        return cart;
    }

    static changeShipping(cart: Cart, item: CartItem): Cart {
        cart.lastAction = CartAction.ItemShippingChanged;
        cart.items = cartItemAdapter.upsertOne(item, cart.items);

        return cart;
    }

    /**
     * Find item in cart by product_id
     *
     * @param {string} product_id
     * @returns {CartItem}
     *
     * @memberOf Cart
     */
    // static findItem(cart: Cart, product_id: string): CartItem {
    //     for (const item of cart.items) {
    //         if (item.product_id === product_id) {
    //             return item;
    //         }
    //     }
    //     return null;
    // }

    /**
     * Find item index by product_id
     *
     * @param {string} product_id
     * @returns {number}
     *
     * @memberOf Cart
     */
    // static findItemIndex(cart: Cart, product_id: string): number {
    //     for (let index = 0; index < cart.items.ids.length; index++) {
    //         const item = cart.items[index];
    //         if (item.product_id === product_id) {
    //             return index;
    //         }
    //     }
    //     return null;
    // }

    /**
     * Merge items of validated with cart
     *
     * @param {CartItem[]} items
     *
     * @memberOf Cart
     */
    static mergeItems(cart: Cart, items: CartItem[], recalculate: boolean = true) {
        // console.log('MERGE', { cart }, { items });

        for (const item of items) {
            if (item.product && cart.items.entities[item.product_id]) {
                cart.items.entities[item.product_id].product.rates.base.precioMostrarBusqueda =
                    item.product.rates.base.precioMostrarBusqueda;
                cart.items.entities[item.product_id].product.rates.base.precioSinDescuento =
                    item.product.rates.base.precioSinDescuento;
                cart.items.entities[item.product_id].product.rates.base.preDto1 = item.product.rates.base.preDto1;
                cart.items.entities[item.product_id].product.rates.base.preDto1 = item.product.rates.base.preDto1;
                cart.items.entities[item.product_id].price = item.price;
                cart.items.entities[item.product_id].priceO = item.priceO;
                cart.items.entities[item.product_id].status = CartItemStatus.Validated;
                cart.items.entities[item.product_id].totalLinea = item.totalLinea;
                cart.items.entities[item.product_id].portesTipoEntrega = item.portesTipoEntrega;
            }
        }

        const cartItems = CartHelper.getItems(cart);

        // Remove obsolete items
        for (const item of cartItems) {
            if (item.status === CartItemStatus.Validating) {
                CartHelper.removeItem(cart, item);
            }
        }

        CartHelper.calculate(cart, recalculate);
    }

    /**
     * Calculate total of cart
     *
     * @private
     *
     * @memberOf Cart
     */
    static calculate(cart: Cart, recalculate: boolean = true): void {
        cart.total = 0;
        cart.totalMostrar = 0;
        cart.numItems = 0;
        // eslint-disable-next-line guard-for-in
        for (const product_id in cart.items.entities) {
            const item = cart.items.entities[product_id];

            cart.total += CartItemHelper.total(item, recalculate);
            cart.totalMostrar += CartItemHelper.totalMostrar(item);
            cart.numItems += item.quantity;
        }

        if (cart.numItems) {
            let promo: any = {};
            for (promo of cart.promos) {
                cart.total += promo.total;
            }
        }
    }

    static cleanValidatingCartItems(cart: Cart): void {
        const cartItems = CartHelper.getItems(cart);

        // Remove not validated items
        for (const item of cartItems) {
            if (item.status !== CartItemStatus.Validated) {
                CartHelper.removeItem(cart, item);
            }
        }
    }
}
